
import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

const quickLinks = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Programs', path: '/programs' },
  { label: 'Impact', path: '/impact' },
  { label: 'Donate', path: '/donate' },
  { label: 'Contact', path: '/contact' },
];

const socials = {
  facebook: "https://www.facebook.com/share/1GnuTJBV65/",
  twitter: "https://x.com/elhaiminis7704",
  instagram: "https://www.instagram.com/elhaiministries/",
  youtube: "https://www.youtube.com/@elhaiministries",
  pinterest: "https://www.pinterest.com/elhaiministries/",
  tiktok: "https://www.tiktok.com/@elhaiministries?lang=en",
  linkedin: "https://www.linkedin.com/in/elhaiministries-undefined-270aab3bb/",
};

const programs = [
  'Financial Inclusion Program',
  'ICT Training',
  'Music Training',
  'Tailoring Skills',
  'Welding & Adjustment',
  'Food Security',
  'Health Counseling',
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletter = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append('email', email);
      await fetch('', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#003366] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-white flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/cc883008a4d7bcfeafa6a2e5816cdde1/37b170ad110bf8756df40953dd95ddb8.jpeg"
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="font-bold text-sm leading-tight">EL HAI PENTECOSTAL</p>
                <p className="text-[#ADD8E6] text-xs tracking-wider">MINISTRIES</p>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Empowering communities through, education, and sustainable development across Uganda.
            </p>
            <div className="flex gap-3">
              {Object.entries(socials).map(([social, link]) => (
                <a
                  key={social}
                  href={link}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#ADD8E6] hover:text-[#003366] text-white transition-colors cursor-pointer"
                  aria-label={social}
                >
                  <i className={`ri-${social}-fill text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-[#ADD8E6] uppercase tracking-widest text-xs mb-5">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/70 text-sm hover:text-[#ADD8E6] transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="font-semibold text-[#ADD8E6] uppercase tracking-widest text-xs mb-5">
              Our Programs
            </h4>
            <ul className="space-y-2">
              {programs.map((prog) => (
                <li key={prog}>
                  <Link
                    to="/programs"
                    className="text-white/70 text-sm hover:text-[#ADD8E6] transition-colors cursor-pointer"
                  >
                    {prog}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Newsletter */}
          <div>
            <h4 className="font-semibold text-[#ADD8E6] uppercase tracking-widest text-xs mb-5">
              Contact Us
            </h4>
            <ul className="space-y-3 mb-6">
              <li className="flex gap-3 text-sm text-white/70">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-[#ADD8E6]" />
                </div>
                P.O. Box 980190, Kajjansi, Uganda
              </li>
              <li className="flex gap-3 text-sm">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-mail-line text-[#ADD8E6]" />
                </div>
                <a
                  href="mailto:hello@elhaiministries.org"
                  className="text-[#ADD8E6] hover:underline cursor-pointer"
                >
                  hello@elhaiministries.org
                </a>
              </li>
            </ul>

            <h4 className="font-semibold text-[#ADD8E6] uppercase tracking-widest text-xs mb-3">
              Stay Updated
            </h4>
            {submitted ? (
              <p className="text-[#ADD8E6] text-sm font-medium">
                Thank you for subscribing!
              </p>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleNewsletter}
                className="flex gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="flex-1 bg-white/10 border border-white/20 rounded-md px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#ADD8E6] min-w-0"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3 py-2 bg-[#ADD8E6] text-[#003366] font-semibold text-sm rounded-md hover:bg-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? '...' : 'Join'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-white/50 text-xs">
            &copy; {new Date().getFullYear()} El Hai Pentecostal Ministries. All rights reserved.
          </p>
          <p className="text-white/50 text-xs">
            Kajjansi, Uganda &bull; Faith &bull; Hope &bull; Community
          </p>
        </div>
      </div>
    </footer>
  );
}
